import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchPreOpChecklistData, 
  fetchPatientData, 
  fetchPreOpNextIp, 
  savePreOpChecklist, 
  updatePreOpChecklist 
} from "../api/preOpChecklistApi";
import { mapBackendToForm, mapFormToBackend } from "../utils/mapping";
import { useFormValidation } from "./useFormValidation";
import { useNotification } from "../../../../contexts/NotificationContext";
import { DRAFT_KEYS, loadDraft, clearDraft } from "../../../../utils/draftUtils";

export const usePreOpChecklist = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [success, setSuccess] = useState(false);
  
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    name: "",
    age: "",
    sex: "",
    blood_group: "",
    weight: "",
    room_no: "",
    consultant: "",
    diagnosis: "",
    date: "",
    consent_high_risk: "",
    consent_high_risk_remarks: "",
    consent_pre_operative: "",
    consent_pre_operative_remarks: "",
    preparation_of_parts: "",
    preparation_of_parts_remarks: "",
    surgical_site_marked: "",
    surgical_site_marked_remarks: "",
    jewels_removed: "",
    jewels_removed_remarks: "",
    hearing_aid_removed: "",
    hearing_aid_removed_remarks: "",
    starvation_from: "",
    starvation_from_remarks: "",
    bowel_preparation: "",
    bowel_preparation_remarks: "",
    bowel_preparation_time: "",
    bowel_preparation_medication: "",
    investigation_xray_ct_ecg: "",
    investigation_xray_ct_ecg_remarks: "",
    premedication: "",
    premedication_remarks: "",
    blood_requirements: "",
    blood_requirements_remarks: "",
    blood_units: "",
    ot_dress_removed: "",
    ot_dress_removed_remarks: "",
    allergies: "",
    allergies_remarks: "",
    specify: "",
    specify_remarks: "",
    iv_fluids_drugs_given: "",
    iv_fluids_drugs_given_remarks: "",
    ot_drugs_file_handed: "",
    ot_drugs_file_handed_remarks: "",
    inj_tt_test_dose: "",
    inj_tt_test_dose_remarks: "",
    case_sheet_checked: "",
    case_sheet_checked_remarks: "",
    anti_coagulation: "",
    anti_coagulation_remarks: "",
    hbsag_hcv_hiv: "",
    hbsag_hcv_hiv_remarks: "",
    bladder_voided: "",
    bladder_voided_remarks: "",
    loose_tooth_removed: "",
    loose_tooth_removed_remarks: "",
    on_any_medication: "",
    on_any_medication_remarks: "",
    medication_details: "",
    vital_pr: "No",
    vital_pr_remarks: "",
    vital_bp: "No",
    vital_bp_remarks: "",
    vital_o2sat: "No",
    vital_o2sat_remarks: "",
    epidural: "",
    epidural_remarks: "",
    central_line: "",
    central_line_remarks: "",
    feeding_jejunostomy: "",
    feeding_jejunostomy_remarks: "",
    tracheostomy: "",
    tracheostomy_remarks: "",
    icd: "",
    icd_remarks: "",
    drain: "",
    drain_remarks: "",
    ryle_tube: "",
    ryle_tube_remarks: "",
    checklist_time: "",
    ward_nurse_signature: "",
    ot_nurse_signature: "",
    fild_completed_name: "",
    fild_chaged_name: "",
    file_completed_signature: "",
    charge_nurse_signature: "",
    shifted_to_ot_at: "",
    handed_over_by: "",
    received_in_ot_at: "",
    taken_over_by: "",
  });

  const { errors, clearError, validateSubmit, setErrors } = useFormValidation(formData);

  const { data: patientInfo } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  const { data: nextIpData } = useQuery({
    queryKey: ['preOpNextIp'],
    queryFn: fetchPreOpNextIp,
    enabled: !success
  });

  const { data: existingData, isLoading: dataLoading } = useQuery({
    queryKey: ['preOpChecklist', uhid_no],
    queryFn: () => fetchPreOpChecklistData(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      const mapped = mapBackendToForm(existingData);
      setFormData(prev => ({ ...prev, ...mapped }));
      setSuccess(true);
    }
  }, [existingData]);

  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({
        ...prev,
        name: patientInfo.name || prev.name,
        age: patientInfo.age || prev.age,
        sex: patientInfo.sex || prev.sex
      }));
    }
  }, [patientInfo]);

  useEffect(() => {
    if (nextIpData && !success) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, success]);

  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.PREOPERATIVE_CHECKLIST, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no]);

  const handleChange = useCallback((e) => {
    if (!e || !e.target) return;
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? "Yes" : "No") : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      clearError(name);
    }
  }, [errors, clearError]);

  const handleAutocompleteChange = useCallback((name, value) => {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) clearError(name);
  }, [errors, clearError]);

  const handleNameChange = useCallback((e) => {
    const regex = /^[a-zA-Z\s.-]*$/; 
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [e.target.name]: value }));
      if (errors[e.target.name]) {
        clearError(e.target.name);
      }
    }
  }, [errors, clearError]);

  const handleCheckboxChange = useCallback((item, value) => {
    setFormData(prev => ({
      ...prev,
      [item]: value
    }));
  }, []);

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  const handleSignatureClear = () => {
    if (signaturePadRef.current) {
        signaturePadRef.current.clear();
    }
  };

  const handleSignatureSave = (dataURL) => {
    if (dataURL) {
        setFormData(prev => ({
            ...prev,
            [currentSignatureRole]: dataURL.split(',')[1],
        }));
        setSignatureDialogOpen(false);
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    // Basic blur validation, detailed validation on submit
    let error = "";
    if (['date', 'consultant', 'room_no'].includes(name) && !value) {
        error = `${name === 'room_no' ? 'Room No' : name} is required`;
        setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updatePreOpChecklist(uhid_no, data);
      } else {
        return savePreOpChecklist(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success 
        ? 'Pre-Operative Checklist updated successfully!' 
        : 'Pre-Operative Checklist submitted successfully!');
      
      queryClient.invalidateQueries(['preOpChecklist', uhid_no]);
      setSuccess(true);
      clearDraft(DRAFT_KEYS.PREOPERATIVE_CHECKLIST, uhid_no);
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (error) => {
      console.error('Error submitting the form!', error);
      showNotification('error', 'Error in submitting Pre-Operative Checklist!');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mutation.isLoading) return;

    if (!validateSubmit()) {
      return;
    }

    const backendData = mapFormToBackend(formData);
    mutation.mutate(backendData);
  };

  return {
    formData,
    setFormData,
    errors,
    loading: dataLoading || mutation.isLoading,
    success,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleAutocompleteChange,
    handleNameChange,
    handleCheckboxChange,
    handleSignatureOpen,
    handleSignatureClose,
    handleSignatureClear,
    handleSignatureSave,
    handleBlur,
    handleSubmit
  };
};
